import { useEffect, useMemo, useState } from "react";
import { usePaymentQueries } from "@/hooks/usePaymentQueries";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PaymentListDto,
  PaymentProofDto,
} from "@/services/admin-payment.service";
import { formatDateTime } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { proofDownloadPath } from "@/lib/paymentProof";
import {
  MoreVertical,
  Eye,
  Check,
  X,
  RefreshCw,
  ZoomIn,
  StickyNote,
} from "lucide-react";
import { VerificationModal } from "./VerificationModal";
import { RejectionModal } from "./RejectionModal";
import { RefundPaymentDialog } from "./RefundPaymentDialog";
import { PaymentNoteDialog } from "./PaymentNoteDialog";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { PaymentProofLightbox } from "./PaymentProofLightbox";
import { ProofThumbnail } from "./ProofThumbnail";
import { BulkCancelPaymentsDialog } from "./BulkCancelPaymentsDialog";
import { PermissionGate } from "@/components/common/PermissionGate";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import type { BulkCancelPaymentsResult } from "@/services/admin-payment.service";
import { Ban } from "lucide-react";
import { isRefundCandidate } from "@/lib/paymentRefundability";
import {
  PAYMENT_COLUMNS,
  usePaymentColumnsStore,
  type PaymentColumnId,
} from "@/stores/paymentColumnsStore";

/**
 * How wide each column is drawn.
 *
 * Kept here rather than in the store because a width is presentation, not preference — the store
 * holds only what gets written to localStorage. Typing it as a full `Record` over the id union is
 * the point: adding a column to `PAYMENT_COLUMNS` without giving it a width is a compile error,
 * not a column that silently collapses.
 *
 * Fixed px (via the spacing scale), not percentages: Tenant carries a full 36-character GUID and
 * User stacks a name, an e-mail and a phone number, so the excess becomes a horizontal scrollbar
 * instead of squeezing every column into the viewport.
 */
const COLUMN_WIDTHS: Record<PaymentColumnId, string> = {
  index: "w-20",
  paymentNumber: "w-64",
  billCode: "w-40",
  order: "w-56",
  user: "w-64",
  tenant: "w-72",
  proof: "w-24",
  amount: "w-32",
  method: "w-28",
  createdAt: "w-40",
  verifiedAt: "w-40",
  note: "w-64",
  actions: "w-20",
};

function ProofStackUI({
  proofs,
  settlesWithoutProof,
}: {
  proofs: PaymentProofDto[];
  settlesWithoutProof: boolean;
}) {
  const [openLightbox, setOpenLightbox] = useState(false);

  // "No receipt" reads as something missing. For a trial or a balance settlement nothing is
  // missing: neither has a step where a customer could send one, so the row gets the em dash that
  // means "not applicable here" instead of a nudge to go chasing a slip that cannot exist.
  if (settlesWithoutProof) {
    return <span className="text-xs text-muted-foreground/70">—</span>;
  }

  // Not an em dash. Half this table is AwaitingVerification, and for those rows the useful fact
  // is that the customer has not sent a receipt yet — which is why the operator cannot act. A
  // bare "—" made "nothing uploaded" and "something is here but it did not load" look alike.
  if (proofs.length === 0) {
    return <span className="text-xs text-muted-foreground/70">No receipt</span>;
  }

  const lightboxImages = proofs.map((proof, index) => ({
    src: proofDownloadPath(proof.downloadUrl),
    alt: `Payment Proof ${index + 1}`,
    fileName: proof.fileName,
    contentType: proof.contentType,
  }));

  // One tile, whatever the count. Three tiles plus a counter ran past the column and spilled
  // into the amount beside it, and a row of 40px squares says no more than a badge does: the
  // operator opens the lightbox either way, and that is where every slip is legible.
  const [firstProof] = proofs;
  const hiddenCount = proofs.length - 1;

  return (
    <>
      <button
        type="button"
        className="group inline-flex cursor-pointer items-center justify-center gap-1 rounded-md p-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        onClick={() => setOpenLightbox(true)}
        aria-label={`View ${proofs.length} proof file${proofs.length > 1 ? "s" : ""}`}
        title={`Click to view ${proofs.length} proof file${proofs.length > 1 ? "s" : ""}`}
      >
        <span
          // transition-shadow, not transition-all: the only thing hover changes is the ring,
          // which IS a box-shadow, and `all` had the browser watching every animatable
          // property on every tile of a fifty-row table.
          className="relative block h-10 w-10 shrink-0 overflow-hidden rounded-md border border-border/70 bg-muted shadow-sm ring-0 ring-primary/40 transition-shadow group-hover:ring-2"
        >
          {/* Not `compact`: at 40px the label still fits, and it is the label that separates
              a PDF receipt from an image that failed to load — two states that were both just
              a grey square before. */}
          {/* 40px tile, so 96 covers a 2x screen with a little room. */}
          <ProofThumbnail proof={firstProof} thumbnailWidth={96} />

          {/* How many more are behind this one, not the total: the slip on screen is one of them,
              so "+2" on a tile reads as three files the way it does everywhere else. */}
          {hiddenCount > 0 && (
            // Fades out under the hover state rather than stacking with it: two 55% scrims on
            // one tile read as a black square with a number faintly behind the zoom icon.
            <span className="absolute inset-0 flex items-center justify-center bg-ink/55 text-xs font-semibold text-white transition-opacity group-hover:opacity-0">
              +{hiddenCount}
            </span>
          )}

          {/* The tile never said it was clickable — no cursor cue survives a screenshot, and an
              operator who does not try it never finds the lightbox at all. */}
          <span className="absolute inset-0 flex items-center justify-center bg-ink/55 opacity-0 transition-opacity group-hover:opacity-100">
            <ZoomIn className="h-4 w-4 text-white" />
          </span>
        </span>
      </button>

      {openLightbox && (
        <PaymentProofLightbox
          images={lightboxImages}
          initialIndex={0}
          open={openLightbox}
          onOpenChange={setOpenLightbox}
        />
      )}
    </>
  );
}

/**
 * Who did the thing the surrounding column is dated by.
 *
 * The e-mail alone, not a display name: it is what operators recognise each other by, it is unique
 * where a name is not, and an account with no first/last name set would render a blank line. The
 * raw `verifiedBy`/`createdBy` GUID is never shown - an operator cannot read one, and an account
 * that has since been deleted has no e-mail to resolve to (13 payments on dev are in exactly that
 * state), so the honest answer there is nothing rather than a hex string.
 */
function ActorLine({ email }: { email?: string }) {
  if (!email) {
    return null;
  }

  return (
    <div className="truncate" title={email}>
      {email}
    </div>
  );
}

/**
 * Whether a row may be picked for the bulk cancellation.
 *
 * The same rule the backend applies per payment, mirrored here so a selection cannot be built out
 * of rows that are guaranteed to come back skipped. It is a convenience, not the guard: the backend
 * re-reads every one of them, and a payment an operator verifies between the render and the request
 * is still skipped there and named in the result.
 *
 * Anything past Pending / AwaitingVerification is left alone — re-cancelling a Failed, Cancelled or
 * Refunded row would overwrite the note saying why it closed the first time, and a Completed one is
 * money that arrived.
 */
function canSelectForCancellation(payment: PaymentListDto): boolean {
  return (
    payment.status === "Pending" || payment.status === "AwaitingVerification"
  );
}

interface PaymentListTableProps {
  payments: PaymentListDto[];
  /** Row number of the first item, e.g. (page - 1) * pageSize */
  startIndex?: number;
  /**
   * Rows are on their way and there is nothing to show yet — a filter or page change, which
   * discards the previous rows on purpose (see `usePaymentsList`). Without this the region fell
   * back to the empty state and read "No payments found" about a query still in flight.
   */
  isLoading?: boolean;
  /**
   * Changing this drops the row selection. The page bumps it from Refresh, which re-reads the
   * list the operator is looking at — leaving rows ticked across that would carry a selection
   * built against the previous answer into the next bulk cancellation.
   */
  selectionResetKey?: number;
  onViewDetails: (paymentId: string) => void;
}

export function PaymentListTable({
  payments,
  startIndex = 0,
  isLoading = false,
  selectionResetKey = 0,
  onViewDetails,
}: PaymentListTableProps) {
  const { verifyPayment, rejectPayment, bulkCancelPayments } =
    usePaymentQueries();
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentListDto | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkCancelPaymentsResult | null>(
    null,
  );
  // Frozen the moment the dialog opens (see the "Cancel selected" click below). The mutation's
  // own `onSettled` refetches the list before the mutate-level `onSuccess` in
  // `handleConfirmBulkCancel` runs, and that refetch can prune `selectedIds` (via the effect
  // above) while the dialog is still open — a live `selectedPayments` would empty out from under
  // a request that has not reported back yet, and the dialog would read "Cancel 0 selected
  // payments?" underneath its own BusyOverlay.
  const [frozenBulkPayments, setFrozenBulkPayments] = useState<
    PaymentListDto[]
  >([]);
  // The refund mutation lives inside RefundPaymentDialog, so its pending state is mirrored up here
  // for the table overlay below. PaymentNoteDialog reports its own the same way.
  const [refundPending, setRefundPending] = useState(false);
  const [notePending, setNotePending] = useState(false);

  const rowBusyLabel = bulkCancelPayments.isPending
    ? "Cancelling payments…"
    : verifyPayment.isPending
      ? "Verifying payment…"
      : rejectPayment.isPending
        ? "Rejecting payment…"
        : refundPending
          ? "Processing refund…"
          : notePending
            ? "Saving note…"
            : null;

  // Which columns are on screen. Subscribed here, set from the page's Columns menu — both talk to
  // the same store rather than threading props through.
  //
  // Subscribing to `hidden` and deriving the predicate locally, NOT subscribing to the store's own
  // `isVisible`: that one is a stable function reading the latest state, so a component that
  // subscribes to it re-renders for nothing that changed and never re-renders when a column is
  // toggled. `hidden` is the value that actually moves.
  const hiddenColumns = usePaymentColumnsStore((state) => state.hidden);
  const isVisible = (id: PaymentColumnId) => !hiddenColumns.includes(id);

  const selectableIds = useMemo(
    () =>
      payments
        .filter(canSelectForCancellation)
        .map((payment) => payment.paymentId),
    [payments],
  );

  // A row that leaves the page — filtered away, or settled and refetched — must leave the
  // selection with it, or the next batch would carry ids the operator can no longer see.
  useEffect(() => {
    setSelectedIds((previous) => {
      const stillVisible = previous.filter((id) => selectableIds.includes(id));

      // Same array unless something actually dropped out: returning a new one on every render
      // would loop, since this effect depends on the ids it sets.
      return stillVisible.length === previous.length ? previous : stillVisible;
    });
  }, [selectableIds]);

  // Drops the selection when the page asks for a reset. Separate from the pruning effect above:
  // that one answers "these rows are gone", this one answers "the operator asked for a fresh
  // read". A row that survives a Refresh unchanged would otherwise stay ticked against an answer
  // the operator has not looked at yet.
  useEffect(() => {
    setSelectedIds([]);
  }, [selectionResetKey]);

  const selectedPayments = useMemo(
    () => payments.filter((payment) => selectedIds.includes(payment.paymentId)),
    [payments, selectedIds],
  );

  const allSelectableSelected =
    selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  const toggleRow = (paymentId: string, checked: boolean) => {
    setSelectedIds((previous) =>
      checked
        ? [...previous, paymentId]
        : previous.filter((id) => id !== paymentId),
    );
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? selectableIds : []);
  };

  // One dialog for the selection bar and a row's own menu: either way it is the same endpoint, and
  // the rows are frozen here so the dialog lists exactly what will be sent.
  const openCancelDialog = (paymentsToCancel: PaymentListDto[]) => {
    setBulkResult(null);
    setFrozenBulkPayments(paymentsToCancel);
    setBulkDialogOpen(true);
  };

  const handleConfirmBulkCancel = (reason: string) => {
    bulkCancelPayments.mutate(
      {
        paymentIds: frozenBulkPayments.map((payment) => payment.paymentId),
        reason,
      },
      {
        onSuccess: (result) => {
          // Only the payments that actually closed leave the selection. What is left is exactly
          // what was skipped, so the operator can look at those rows without re-picking them.
          const cancelled = new Set(
            result.results
              .filter((row) => row.success)
              .map((row) => row.paymentId),
          );
          setSelectedIds((previous) =>
            previous.filter((id) => !cancelled.has(id)),
          );

          if (result.failureCount === 0) {
            setBulkResult(null);
            setBulkDialogOpen(false);
            return;
          }

          // Kept open on a partial batch: which rows were skipped, and why, is more than a toast
          // can hold and is the only thing that says what to do next.
          setBulkResult(result);
        },
      },
    );
  };

  const handleVerify = (payment: PaymentListDto) => {
    setSelectedPayment(payment);
    setVerifyDialogOpen(true);
  };

  const handleReject = (payment: PaymentListDto) => {
    setSelectedPayment(payment);
    setRejectDialogOpen(true);
  };

  const handleRefund = (payment: PaymentListDto) => {
    setSelectedPayment(payment);
    setRefundDialogOpen(true);
  };

  const handleEditNote = (payment: PaymentListDto) => {
    setSelectedPayment(payment);
    setNoteDialogOpen(true);
  };

  const handleVerifySubmit = (
    notes: string | undefined,
    receivedAmount: number,
  ) => {
    if (!selectedPayment) return;
    verifyPayment.mutate(
      { paymentId: selectedPayment.paymentId, notes, receivedAmount },
      {
        onSuccess: () => {
          setVerifyDialogOpen(false);
          setSelectedPayment(null);
        },
      },
    );
  };

  const handleRejectSubmit = (reason: string) => {
    if (!selectedPayment) return;
    rejectPayment.mutate(
      { paymentId: selectedPayment.paymentId, reason },
      {
        onSuccess: () => {
          setRejectDialogOpen(false);
          setSelectedPayment(null);
        },
      },
    );
  };

  // Placeholder rows rather than a collapsed strip: the region keeps the height it will have once
  // the rows land, so the toolbar above and the pagination below do not jump when they do.
  if (isLoading) {
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">No payments found</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {selectedIds.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
          {/* One text node, not a bolded count next to a string: a screen reader and a test both
              read this as one sentence, and splitting it makes "2 payments selected" unfindable. */}
          <p className="text-sm font-medium">
            {`${selectedIds.length} payment${selectedIds.length === 1 ? "" : "s"} selected`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds([])}
              disabled={bulkCancelPayments.isPending}
            >
              Clear
            </Button>
            {/* Same roles the endpoint accepts, so the button is never shown to someone who would
                only get a 403 back. */}
            <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
              {/* Outline in destructive ink, like the other bulk bars (resources, OS images): a solid
                  red fill shouted louder than anything else on the page for a step that still asks
                  for confirmation. */}
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-destructive-ink hover:border-destructive hover:bg-destructive-soft"
                onClick={() => openCancelDialog(selectedPayments)}
                disabled={bulkCancelPayments.isPending}
              >
                <Ban className="h-4 w-4" />
                Cancel selected
              </Button>
            </PermissionGate>
          </div>
        </div>
      )}

      {/*
        `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
        table (`<div class="relative w-full overflow-auto">`), and it is what makes the horizontal
        scrollbar reachable. That wrapper is as tall as the whole table, so while IT owned the
        horizontal overflow its scrollbar rendered below the last row — off the bottom of the
        viewport, where nobody could reach it. Switching it off hands both axes to this div, the one
        with a bounded height, so the bar sits at the bottom of the visible table. It also repairs
        the sticky header below: `position: sticky` resolves against the nearest scrollport, and
        while that was the inner wrapper — a div that never scrolls vertically — the header simply
        scrolled away. Same change as the resource list.
      */}
      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
        <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
          {/* Generated from PAYMENT_COLUMNS rather than written out, so a hidden column cannot leave
            its <col> behind — which would shift every width after it by one and is the one drift
            the browser gives no error for. A fixed-layout table is widened to the sum of these
            widths, so no `min-w` is needed to make it overflow — only a scrollport that puts the
            bar somewhere visible. */}
          <colgroup>
            {PAYMENT_COLUMNS.filter((column) => isVisible(column.id)).map(
              (column) => (
                <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
              ),
            )}
          </colgroup>
          <TableHeader className="sticky top-0 z-30 bg-card">
            <TableRow>
              {/* Selection shares the STT column instead of owning a bordered sliver of its own: a
                12px unlabelled column reads as a rendering fault, and the row number is exactly
                the thing an operator points at when they say which rows they ticked.
                The header box selects every row on THIS page that can actually be cancelled, not
                every row — one that ticks rows the backend will skip turns "select all" into a
                list of refusals. */}
              {isVisible("index") && (
                <TableHead>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allSelectableSelected}
                      disabled={selectableIds.length === 0}
                      onCheckedChange={(checked) => toggleAll(checked === true)}
                      aria-label="Select all cancellable payments on this page"
                      className="cursor-pointer"
                    />
                    <span>STT</span>
                  </div>
                </TableHead>
              )}
              {isVisible("paymentNumber") && (
                <TableHead>Payment Number</TableHead>
              )}
              {isVisible("billCode") && <TableHead>Bill Code</TableHead>}
              {isVisible("order") && <TableHead>Order</TableHead>}
              {isVisible("user") && <TableHead>User</TableHead>}
              {isVisible("tenant") && <TableHead>Tenant</TableHead>}
              {isVisible("proof") && (
                <TableHead className="text-center">Proof</TableHead>
              )}
              {isVisible("amount") && (
                <TableHead className="text-right">Amount</TableHead>
              )}
              {isVisible("method") && <TableHead>Method</TableHead>}
              {isVisible("verifiedAt") && <TableHead>Verified</TableHead>}
              {isVisible("createdAt") && <TableHead>Created</TableHead>}
              {isVisible("note") && <TableHead>Note</TableHead>}
              {isVisible("actions") && (
                <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment, index) => (
              <TableRow key={payment.paymentId}>
                {isVisible("index") && (
                  <TableCell>
                    <div
                      className="flex items-center gap-2"
                      title={
                        canSelectForCancellation(payment)
                          ? undefined
                          : `A ${payment.status} payment cannot be cancelled.`
                      }
                    >
                      <Checkbox
                        checked={selectedIds.includes(payment.paymentId)}
                        disabled={!canSelectForCancellation(payment)}
                        onCheckedChange={(checked) =>
                          toggleRow(payment.paymentId, checked === true)
                        }
                        aria-label={`Select ${payment.paymentOrderNumber}`}
                        className="cursor-pointer"
                      />
                      <span className="tabular-nums text-muted-foreground">
                        {startIndex + index + 1}
                      </span>
                    </div>
                  </TableCell>
                )}
                {isVisible("paymentNumber") && (
                  <TableCell className="font-medium">
                    <button
                      onClick={() => onViewDetails(payment.paymentId)}
                      className="block max-w-full cursor-pointer truncate text-left font-medium text-primary hover:underline"
                    >
                      {payment.paymentOrderNumber}
                    </button>
                    {/* The status belongs to this number, so it sits under it rather than in a
                        column of its own three columns away. */}
                    <div className="mt-1">
                      <PaymentStatusBadge status={payment.status} />
                    </div>
                  </TableCell>
                )}
                {isVisible("billCode") && (
                  <TableCell>
                    <span className="select-all font-mono text-xs">
                      {payment.tPayGateBillCode || "—"}
                    </span>
                  </TableCell>
                )}
                {isVisible("order") && (
                  <TableCell>
                    {/* A payment can stand on its own (a balance top-up), and that gets the em
                        dash the rest of this table already uses for "not applicable here" - the
                        same mark the resource list's Order column uses, and the same one the Proof
                        column two cells over uses for a trial. It used to read "No order", which
                        spent a line of prose on the unremarkable half of the column.

                        Order TYPE, not order status: the payment's own status is already under its
                        number, and the order's largely restates it, while "NewService" vs
                        "Renewal" says why this payment exists at all - and it is the only place on
                        the screen that does. Same call as the resource list's Order column. */}
                    {payment.orderNumber || payment.orderType ? (
                      <>
                        <div
                          className="truncate font-medium"
                          title={payment.orderNumber ?? undefined}
                        >
                          {payment.orderNumber ?? "—"}
                        </div>
                        {payment.orderType && (
                          <Badge
                            variant="outline"
                            className="mt-1 px-1.5 py-0 text-[11px] text-muted-foreground"
                          >
                            {payment.orderType}
                          </Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                )}
                {isVisible("user") && (
                  <TableCell>
                    <div className="truncate font-medium">
                      {payment.ownerName || "—"}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {payment.ownerEmail}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {payment.ownerPhone || "—"}
                    </div>
                  </TableCell>
                )}
                {isVisible("tenant") && (
                  <TableCell>
                    <div className="truncate font-medium">
                      {payment.tenantName}
                    </div>
                    <div
                      className="truncate font-mono text-[11px] text-muted-foreground"
                      title={payment.tenantId}
                    >
                      {payment.tenantId}
                    </div>
                  </TableCell>
                )}
                {isVisible("proof") && (
                  <TableCell className="text-center">
                    <ProofStackUI
                      proofs={payment.proofs ?? []}
                      settlesWithoutProof={
                        payment.paymentMethod === "Trial" ||
                        payment.paymentMethod === "Balance"
                      }
                    />
                  </TableCell>
                )}
                {isVisible("amount") && (
                  <TableCell className="text-right font-medium">
                    {formatCurrency(payment.amount, payment.currency)}
                  </TableCell>
                )}
                {isVisible("method") && (
                  <TableCell>
                    <Badge variant="outline" className="truncate">
                      {payment.paymentMethod}
                    </Badge>
                  </TableCell>
                )}
                {isVisible("verifiedAt") && (
                  <TableCell className="text-xs text-muted-foreground">
                    {payment.verifiedAt ? (
                      <>
                        <div className="truncate">
                          {formatDateTime(payment.verifiedAt)}
                        </div>
                        <ActorLine email={payment.verifiedByEmail} />
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
                {isVisible("createdAt") && (
                  <TableCell className="text-xs text-muted-foreground">
                    <div className="truncate">
                      {formatDateTime(payment.createdAt)}
                    </div>
                    <ActorLine email={payment.createdByEmail} />
                  </TableCell>
                )}
                {isVisible("note") && (
                  <TableCell className="text-xs">
                    {payment.verificationNotes ? (
                      // Two lines in the row, the whole note in the tooltip: prose is the widest
                      // thing this table carries, and a note that sets its own row height would
                      // make every other row harder to scan.
                      <span
                        className="line-clamp-2 whitespace-pre-wrap"
                        title={payment.verificationNotes}
                      >
                        {payment.verificationNotes}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/70">—</span>
                    )}
                  </TableCell>
                )}
                {isVisible("actions") && (
                  <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                    <div className="flex items-center justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onViewDetails(payment.paymentId)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {/* Offered in every status, Completed included, and gated to the role
                              the endpoint accepts. */}
                          <PermissionGate roles="SuperAdmin">
                            <DropdownMenuItem
                              onClick={() => handleEditNote(payment)}
                            >
                              <StickyNote className="mr-2 h-4 w-4" />
                              {payment.verificationNotes
                                ? "Edit note"
                                : "Add note"}
                            </DropdownMenuItem>
                          </PermissionGate>
                          {(payment.status === "Pending" ||
                            payment.status === "AwaitingVerification") && (
                            <PermissionGate
                              roles={["SuperAdmin", "BillingAdmin"]}
                            >
                              <DropdownMenuItem
                                onClick={() => handleVerify(payment)}
                              >
                                <Check className="mr-2 h-4 w-4" />
                                Verify
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleReject(payment)}
                                className="text-destructive-ink"
                              >
                                <X className="mr-2 h-4 w-4" />
                                Reject
                              </DropdownMenuItem>
                              {/* The selection bar's cancel for one row: same statuses, same roles. */}
                              <DropdownMenuItem
                                onClick={() => openCancelDialog([payment])}
                                disabled={bulkCancelPayments.isPending}
                                className="text-destructive-ink"
                              >
                                <Ban className="mr-2 h-4 w-4" />
                                Cancel payment
                              </DropdownMenuItem>
                            </PermissionGate>
                          )}
                          {/* One rule with PaymentDetailPage; the preview decides — see
                            isRefundCandidate. */}
                          {isRefundCandidate({
                            status: payment.status,
                            paymentMethod: payment.paymentMethod,
                            orderType: payment.orderType,
                            hasOrder: Boolean(payment.orderId),
                          }) && (
                            <PermissionGate roles="SuperAdmin">
                              <DropdownMenuItem
                                onClick={() => handleRefund(payment)}
                                className="text-destructive-ink"
                              >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Refund
                              </DropdownMenuItem>
                            </PermissionGate>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BulkCancelPaymentsDialog
        open={bulkDialogOpen}
        onOpenChange={(next) => {
          setBulkDialogOpen(next);
          if (!next) {
            setBulkResult(null);
            setFrozenBulkPayments([]);
          }
        }}
        payments={frozenBulkPayments}
        isPending={bulkCancelPayments.isPending}
        result={bulkResult}
        onConfirm={handleConfirmBulkCancel}
      />

      {selectedPayment && (
        <>
          <VerificationModal
            open={verifyDialogOpen}
            onOpenChange={setVerifyDialogOpen}
            payment={{
              paymentOrderNumber: selectedPayment.paymentOrderNumber,
              amount: selectedPayment.amount,
              currency: selectedPayment.currency,
              qrProvider: selectedPayment.qrProvider,
              // Already on the row DTO, so the dialog shows the same slips the column does.
              proofs: selectedPayment.proofs,
            }}
            onConfirm={handleVerifySubmit}
            isLoading={verifyPayment.isPending}
            pageOverlay={false}
          />
          <RejectionModal
            open={rejectDialogOpen}
            onOpenChange={setRejectDialogOpen}
            onConfirm={handleRejectSubmit}
            isLoading={rejectPayment.isPending}
            pageOverlay={false}
          />
          {noteDialogOpen && (
            <PaymentNoteDialog
              paymentId={selectedPayment.paymentId}
              paymentOrderNumber={selectedPayment.paymentOrderNumber}
              note={selectedPayment.verificationNotes}
              open={noteDialogOpen}
              onOpenChange={setNoteDialogOpen}
              onPendingChange={setNotePending}
              pageOverlay={false}
            />
          )}
          {selectedPayment.status === "Completed" && (
            <RefundPaymentDialog
              paymentId={selectedPayment.paymentId}
              paymentAmount={selectedPayment.amount}
              paymentCurrency={selectedPayment.currency}
              open={refundDialogOpen}
              onOpenChange={setRefundDialogOpen}
              onPendingChange={setRefundPending}
              pageOverlay={false}
            />
          )}
        </>
      )}

      {/* Every write started from a row or the bulk bar covers the list's Card (the page's
          `relative` Card is the nearest positioned ancestor), not the page: it is the table that
          must not take a second write before the re-read lands. The dialog that started it stays
          above with its own spinner. */}
      <BusyOverlay
        contained
        open={rowBusyLabel !== null}
        label={rowBusyLabel ?? ""}
      />
    </div>
  );
}
